import { FormattedMessage, useIntl } from 'react-intl';
import { runInBackground } from '../../../../configuration/setup/backgroundActions';
import { usePostTransportOrderConfirmationMutation } from '../../api/transportOrderApi';
import { handleQueryError } from '../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../notifications/SuccessNotification';
import { isValidDate } from '../../sharedComponents/dateHelper';
import { MeansOfTransportType } from '../../sharedComponents/common.types';
import { MeansOfTransportFormInput } from '../sidebar/confirmationForm/meansOfTransportInput/MeansOfTransportFormInput';
import { TrailerFormInput } from '../sidebar/confirmationForm/trailerInput/TrailerFormInput';
import { getBulkTransportOrderConfirmationPayload } from '../sidebar/getConfirmationPayload';
import { ExecutionStatus, TransportAssignmentStatus, TransportOrder } from '../transportAssignment.types';
import { BulkUpdateExecutionStatusForm } from './BulkUpdateExecutionStatusForm';
import { TransportOrderBulkUpdateDatePickerFormGroup } from './TransportOrderBulkUpdateDatePickerFormGroup';
import { useBulkUpdateConfirmationForm } from './useBulkUpdateConfirmationForm';
import { ConfirmationButton } from '../shared/ConfirmationButton';
import { DatePickerType } from '../sidebar/TransportAssignmentConfirmationForm';
import { useState } from 'react';

type TransportOrderBulkUpdateFormStepProps = {
    readonly validAssignments: TransportOrder[];
};

// eslint-disable-next-line max-lines-per-function
export const TransportOrderBulkUpdateFormStep = ({ validAssignments }: TransportOrderBulkUpdateFormStepProps) => {
    const intl = useIntl();
    const [confirmTransportOrder, confirmationRequest] = usePostTransportOrderConfirmationMutation();

    const {
        formState,
        displayState,
        validation,
        setMeansOfTransportId,
        setTrailerId,
        setConfirmedPickUpDate,
        setConfirmedDeliveryDate,
    } = useBulkUpdateConfirmationForm(validAssignments);
    const multipleDifferentValues = intl.formatMessage({
        id: 'outboundOrderBook.transportAssignments.bulkUpdate.multipleDifferentValues',
    });
    const isFormDisabled = confirmationRequest.isLoading;

    const [hasInvalidPickUpDateInput, setHasInvalidPickUpDateInput] = useState(false);
    const [hasInvalidDeliveryDateInput, setHasInvalidDeliveryDateInput] = useState(false);

    const meansOfTransport =
        formState.truckLicensePlate !== undefined || formState.trailerLicensePlate !== undefined
            ? {
                  type: MeansOfTransportType.TRUCK,
                  id: formState.truckLicensePlate,
                  trailerId: formState.trailerLicensePlate,
              }
            : undefined;

    const handleConfirmOrder = () => {
        runInBackground(async () => {
            try {
                await confirmTransportOrder(
                    getBulkTransportOrderConfirmationPayload(validAssignments, {
                        newConfirmedPickUpDate: isValidDate(formState.confirmedPickUpDate)
                            ? formState.confirmedPickUpDate
                            : undefined,
                        newConfirmedDeliveryDate: isValidDate(formState.confirmedDeliveryDate)
                            ? formState.confirmedDeliveryDate
                            : undefined,
                        newMeansOfTransport: meansOfTransport,
                        newUnloadingAddress: undefined,
                    }),
                ).unwrap();
                showSuccessNotification('outboundPortal.notification.confirmTransportAssignment.success');
            } catch (error: unknown) {
                handleQueryError(error);
            }
        });
    };

    return (
        <div>
            <div className="border border-color-primary rounded bg-highlight-lightest padding-20">
                <div className="text-medium text-color-highlight">
                    <FormattedMessage id="outboundPortal.transportAssignments.sidebar.expanderTitle.unconfirmed" />
                </div>
                <div className="display-flex align-items-start margin-top-20">
                    <MeansOfTransportFormInput
                        meansOfTransport={meansOfTransport}
                        setMeansOfTransportId={setMeansOfTransportId}
                        validation={validation.meansOfTransport}
                        isFormDisabled={isFormDisabled}
                    />
                    <TrailerFormInput
                        meansOfTransport={meansOfTransport}
                        setTrailerId={setTrailerId}
                        validation={validation.trailer}
                        isFormDisabled={isFormDisabled}
                    />
                </div>
                <hr />
                <div className="display-flex flex-column">
                    <TransportOrderBulkUpdateDatePickerFormGroup
                        datePickerType={DatePickerType.PICKUP_DATE_PICKER}
                        displayFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.requestedPickUpDateInterval"
                        displayFieldValueOverride={
                            displayState.requestedPickUpDate === undefined ? multipleDifferentValues : undefined
                        }
                        inputFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedPickUpDate"
                        displayFieldValue={displayState.requestedPickUpDate}
                        timeZone={displayState.pickUpTimeZone}
                        inputFieldValue={formState.confirmedPickUpDate}
                        onChange={setConfirmedPickUpDate}
                        validation={validation.confirmedPickUpDate}
                        isFormDisabled={isFormDisabled}
                        onParseError={(hasParseError) => setHasInvalidPickUpDateInput(hasParseError)}
                    />
                </div>
                <div className="display-flex flex-column">
                    <TransportOrderBulkUpdateDatePickerFormGroup
                        datePickerType={DatePickerType.DELIVERY_DATE_PICKER}
                        displayFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.requestedDeliveryDate"
                        displayFieldValueOverride={
                            displayState.requestedDeliveryDate === undefined ? multipleDifferentValues : undefined
                        }
                        inputFieldMessageId="outboundPortal.transportAssignments.sidebar.assignmentInformation.confirmedDeliveryDate"
                        displayFieldValue={displayState.requestedDeliveryDate}
                        timeZone={displayState.deliveryTimeZone}
                        inputFieldValue={formState.confirmedDeliveryDate}
                        onChange={setConfirmedDeliveryDate}
                        validation={validation.confirmedDeliveryDate}
                        isFormDisabled={isFormDisabled}
                        onParseError={(hasParseError) => setHasInvalidDeliveryDateInput(hasParseError)}
                    />
                </div>
                <div className="display-flex flex-row-reverse">
                    <ConfirmationButton
                        isUnconfirmed={validAssignments.every(
                            (order) => order.status === TransportAssignmentStatus.UNCONFIRMED,
                        )}
                        isCapacity={false}
                        isLoading={confirmationRequest.isLoading}
                        isButtonDisabled={
                            validation.isButtonDisabled ||
                            isFormDisabled ||
                            hasInvalidPickUpDateInput ||
                            hasInvalidDeliveryDateInput
                        }
                        onConfirm={handleConfirmOrder}
                    />
                </div>
            </div>
            <hr />
            <div className="display-flex flex-wrap bg-lightest gap-20 padding-20">
                <div className="flex-1-1">
                    <BulkUpdateExecutionStatusForm status={ExecutionStatus.LOADED} assignments={validAssignments} />
                </div>
                <div className="flex-1-1">
                    <BulkUpdateExecutionStatusForm status={ExecutionStatus.UNLOADED} assignments={validAssignments} />
                </div>
            </div>
        </div>
    );
};
